import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c209Api, FJ710, FJ711, FJ712 } from '../../../services/c209Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useC209Mode';

export function useC209Record() {
  const [record, setRecord] = useState<FJ710 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ710>>({});
  const [progresses, setProgresses] = useState<Partial<FJ711>[]>([]);
  const [colors, setColors] = useState<Partial<FJ712>[]>([]);
  const [selectedProgressRow, setSelectedProgressRow] = useState(0);
  const [selectedColorRow, setSelectedColorRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { clearFieldErrors, getFieldError, setFieldErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FJ710;
  } | null> => {
    setLoading(true);
    try {
      const data = await c209Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setProgresses(data.progresses || []);
      setColors(data.colors || []);
      setSelectedProgressRow(0);
      setSelectedColorRow(0);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FJ710, value: FJ710[keyof FJ710]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FJ710): FJ710[keyof FJ710] => {
    return (formData as Record<string, FJ710[keyof FJ710]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) [T-F01: use mutation response] */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ710) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ710,
        progresses: progresses as FJ711[],
        colors: colors as FJ712[],
      };
      if (mode === 'add') {
        const result = await c209Api.create(req);
        message.success(`新增成功: ${result.fj710_01}`);
        onSuccess(result);
      } else {
        const result = await c209Api.update(formData.fj710_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setProgresses(result.progresses || []);
        setColors(result.colors || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, progresses, colors, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await c209Api.delete(record.fj710_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a progress row (BR-F08) */
  const addProgressRow = useCallback(() => {
    const maxSeq = progresses.reduce((max, d) => Math.max(max, d.fj711_02 || 0), 0);
    setProgresses(prev => [...prev, {
      fj711_01: formData.fj710_01 || '',
      fj711_02: maxSeq + 1,
      fj711_11: '',
      fj711_12: '',
    }]);
  }, [progresses, formData.fj710_01]);

  /** Delete the selected progress row (BR-F09) [T-F05: reindex] */
  const deleteProgressRow = useCallback(() => {
    if (progresses.length <= 0) return;
    setProgresses(prev => {
      const next = prev.filter((_, i) => i !== selectedProgressRow);
      return next.map((d, i) => ({ ...d, fj711_02: i + 1 }));
    });
    setSelectedProgressRow(Math.max(0, selectedProgressRow - 1));
  }, [progresses, selectedProgressRow]);

  /** Add a color row (BR-F10: max 99) */
  const addColorRow = useCallback(() => {
    const maxSeq = colors.reduce((max, d) => Math.max(max, d.fj712_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('顏色明細最多 99 項');
      return;
    }
    setColors(prev => [...prev, {
      fj712_01: formData.fj710_01 || '',
      fj712_02: maxSeq + 1,
      fj712_11: '',
      fj712_12: 0,
      fj712_13: '',
      fj712_15: '',
      fj712_16: '',
    }]);
  }, [colors, formData.fj710_01]);

  /** Delete the selected color row (BR-F11) [T-F05: reindex] */
  const deleteColorRow = useCallback(() => {
    if (colors.length <= 0) return;
    setColors(prev => {
      const next = prev.filter((_, i) => i !== selectedColorRow);
      return next.map((d, i) => ({ ...d, fj712_02: i + 1 }));
    });
    setSelectedColorRow(Math.max(0, selectedColorRow - 1));
  }, [colors, selectedColorRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setProgresses(record.progresses || []);
      setColors(record.colors || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fj710_80: 'N',
      fj710_151: 0,
      fj710_162: 0,
      fj710_172: 0,
      fj710_181: 0,
      fj710_192: 0,
      fj710_201: 0,
      fj710_27: 0,
      fj710_29: 0,
    });
    setProgresses([]);
    setColors([]);
    setSelectedProgressRow(0);
    setSelectedColorRow(0);
  }, [clearFieldErrors]);

  /** Apply result after save [T-F01] */
  const applyResult = useCallback((result: FJ710) => {
    setRecord(result);
    setFormData({ ...result });
    setProgresses(result.progresses || []);
    setColors(result.colors || []);
  }, []);

  return {
    record, formData, progresses, colors,
    selectedProgressRow, selectedColorRow, loading,
    setSelectedProgressRow, setSelectedColorRow,
    setProgresses, setColors,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addProgressRow, deleteProgressRow,
    addColorRow, deleteColorRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
